import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import EventSettings from './EventSettings';

function Admin({ token, apiUrl, onSettingsChange }) {
  const [whitelist, setWhitelist] = useState([]);
  const [newCallsign, setNewCallsign] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [wipeLoading, setWipeLoading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchWhitelist = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/admin/whitelist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWhitelist(response.data);
    } catch (err) {
      console.error('Error fetching whitelist:', err);
    }
  }, [apiUrl, token]);

  const fetchBackups = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/admin/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(response.data);
    } catch (err) {
      console.error('Error fetching backups:', err);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchWhitelist();
    fetchBackups();
  }, [fetchWhitelist, fetchBackups]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await axios.post(`${apiUrl}/api/admin/whitelist`, {
        callsign: newCallsign,
        isAdmin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(`Added ${newCallsign.toUpperCase()} to whitelist`);
      setNewCallsign('');
      setIsAdmin(false);
      fetchWhitelist();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add callsign');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (callsign) => {
    if (!window.confirm(`Remove ${callsign} from whitelist?`)) return;

    try {
      await axios.delete(`${apiUrl}/api/admin/whitelist/${callsign}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWhitelist();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove callsign');
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(`${apiUrl}/api/admin/backup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`Backup created: ${response.data.filename} (${response.data.contactCount} contacts)`);
      fetchBackups();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/admin/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `aurora_export_${new Date().toISOString().split('T')[0]}.adi`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export ADIF');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const detectAdifContestType = (adifContent) => {
    const CONTEST_ID_TO_TYPE = {
      'WINTER-FIELD-DAY': 'WFD',
      'WFD':              'WFD',
      'ARRL-FIELD-DAY':   'ARFD',
      'ARRL-FD':          'ARFD',
      'FD':               'ARFD',
      'FIELD-DAY':        'ARFD',
    };
    const match = adifContent.match(/<CONTEST_ID:\d+>([^<\r\n]+)/i);
    if (match) {
      const id = match[1].trim().toUpperCase();
      return CONTEST_ID_TO_TYPE[id] || null;
    }
    return null;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    setError('');
    setSuccess('');

    try {
      const adifContent = await file.text();

      // Detect contest type from file; prompt user if ambiguous
      let contestType = detectAdifContestType(adifContent);
      if (!contestType) {
        const choice = window.confirm(
          'Could not detect the contest type from this ADIF file.\n\n' +
          'Click OK for Winter Field Day (WFD)\n' +
          'Click Cancel for ARRL Field Day (ARFD)'
        );
        contestType = choice ? 'WFD' : 'ARFD';
      } else {
        const label = contestType === 'WFD' ? 'Winter Field Day (WFD)' : 'ARRL Field Day (ARFD)';
        const confirmed = window.confirm(
          `Detected contest type: ${label}\n\nProceed with this import?`
        );
        if (!confirmed) {
          e.target.value = '';
          setImportLoading(false);
          return;
        }
      }

      const replaceAll = window.confirm(
        `Replace all existing ${contestType} contacts with this import?\n\n` +
        'Click OK to replace (other contest type contacts are kept).\n' +
        'Click Cancel to add to existing contacts.'
      );

      const response = await axios.post(`${apiUrl}/api/admin/import`, {
        adifContent,
        replaceAll,
        contestType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(`Imported ${response.data.imported} ${contestType} contacts${replaceAll ? ' (replaced existing)' : ''}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import ADIF');
    } finally {
      setImportLoading(false);
      e.target.value = '';
    }
  };

  const handleWipeAllLogs = async () => {
    const confirmText = 'DELETE ALL';
    const userInput = window.prompt(
      `⚠️ WARNING: This will permanently delete ALL contact logs!\n\n` +
      `This action cannot be undone. Consider creating a backup first.\n\n` +
      `Type "${confirmText}" to confirm:`
    );
    
    if (userInput !== confirmText) {
      if (userInput !== null) {
        setError('Deletion cancelled - confirmation text did not match');
      }
      return;
    }
    
    setWipeLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.delete(`${apiUrl}/api/admin/contacts/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(`Deleted ${response.data.deletedCount} contacts`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete contacts');
    } finally {
      setWipeLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <main className="main-content">
      <div className="card">
        <h2 className="card-title">Add to Whitelist</h2>
        <form onSubmit={handleAdd}>
          <div className="form-grid">
            <div className="form-group">
              <label>Callsign</label>
              <input
                type="text"
                value={newCallsign}
                onChange={(e) => setNewCallsign(e.target.value.toUpperCase())}
                placeholder="W1AW"
                autoComplete="off"
              />
            </div>
            
            <div className="form-group">
              <label>&nbsp;</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                Admin privileges
              </label>
            </div>
            
            <div className="form-group" style={{ justifyContent: 'flex-end' }}>
              <button type="submit" className="btn" disabled={loading || !newCallsign.trim()}>
                {loading ? 'Adding...' : 'Add Callsign'}
              </button>
            </div>
          </div>
          
          {error && <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>}
          {success && <div className="success-message" style={{ marginTop: '1rem' }}>{success}</div>}
        </form>
      </div>

      <div className="card">
        <h2 className="card-title">Whitelisted Callsigns ({whitelist.length})</h2>
        <div>
          {whitelist.map(item => (
            <div key={item.callsign} className="whitelist-item">
              <div>
                <span className="whitelist-callsign">{item.callsign}</span>
                {item.is_admin && <span className="admin-badge">ADMIN</span>}
              </div>
              <button
                className="btn btn-danger btn-small"
                onClick={() => handleRemove(item.callsign)}
              >
                Remove
              </button>
            </div>
          ))}
          
          {whitelist.length === 0 && (
            <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem' }}>
              No callsigns in whitelist
            </p>
          )}
        </div>
        
        <EventSettings token={token} apiUrl={apiUrl} onSettingsChange={onSettingsChange} />
      </div>

      <div className="card">
        <h2 className="card-title">Backup & Export</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Automatic backups run every 2 hours. Use manual backup before major changes.
        </p>
        
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <button 
            className="btn" 
            onClick={handleBackup}
            disabled={backupLoading}
          >
            {backupLoading ? 'Creating...' : 'Create Backup'}
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={handleExport}
          >
            Download ADIF
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={handleImportClick}
            disabled={importLoading}
          >
            {importLoading ? 'Importing...' : 'Import ADIF'}
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".adi,.adif"
            style={{ display: 'none' }}
          />
        </div>

        {backups.length > 0 && (
          <>
            <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Recent Backups</h3>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {backups.slice(0, 10).map(backup => (
                <div key={backup.filename} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  padding: '0.5rem 0',
                  borderBottom: '1px solid var(--border-color)',
                  fontSize: '0.875rem'
                }}>
                  <span>{backup.filename}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {formatFileSize(backup.size)} • {formatDate(backup.created)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2 className="card-title" style={{ color: 'var(--error-color)' }}>Danger Zone</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Permanently delete all contact logs. This cannot be undone.
        </p>
        <button 
          className="btn btn-danger" 
          onClick={handleWipeAllLogs}
          disabled={wipeLoading}
        >
          {wipeLoading ? 'Deleting...' : 'Delete All Logs'}
        </button>
      </div>
    </main>
  );
}

export default Admin;
